import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Grid
} from '@material-ui/core';

import {
  fetchItemFamily,
  fetchItemFamilyAwards,
  fetchItemFamilyReviews,
  selectFetchStatus,
  selectAccess,
  selectAccessToRoleBasedFields,
  resetStatus
} from './itemFamilySlice';

import TopSection from '../common/topSection';
import BreadCrumb from '../common/breadcrumb';
import Alert from '../common/alert';
import Loading from '../common/loading';
import PinButton from '../pins/PinButton';

import StatusTabs from './StatusTabs';
import ItemFamilyPagePC1 from './itemFamilyPagePC1';
import ItemFamilyPagePC24 from './itemFamilyPagePC24';
import useTitle from '../../hooks/use-title.hook';


const ownerSpecificComponents = {
  PC1: ItemFamilyPagePC1,
  PC24: ItemFamilyPagePC24
};

const ItemFamily = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { itemOwnerId, itemFamilyId } = useParams();
  const routeMatch = useRouteMatch();
  const { fetchStatus, fetchError, ...itemFamily } = useSelector(selectFetchStatus);
  const canEdit = useSelector(selectAccess);
  const canEditRoleBasedFields = useSelector(selectAccessToRoleBasedFields);

  const breadcrumbItems = [
    {name: itemOwnerId, link: `/pih/owner/${itemOwnerId}`},
    {name: t('nav:itemFamilies'), link: `/pih/owner/${itemOwnerId}/families`},
    itemFamily.draft.name ? {name: itemFamily.draft.name} : {name: itemFamilyId}
  ].filter(Boolean);

  const statusTabs = [
    {id: 0, url: routeMatch.url, label: t('itemFamily:draft')},
    {id: 1, url: `${routeMatch.url}/published`, label: t('itemFamily:published'), disabled: itemFamily.published === null}
  ];

  useEffect(() => {
    // Fetch data from server
    dispatch(fetchItemFamily({ownerId: itemOwnerId, itemFamilyId: itemFamilyId}));

    if(itemOwnerId === 'PC1') {
      // Tyre awards only in pc1
      dispatch(fetchItemFamilyAwards({itemOwnerId, itemFamilyId}));
      dispatch(fetchItemFamilyReviews({itemOwnerId, itemFamilyId}));
    }

    return () => {
      // Empty state on unmount
      // ckeditor causes error if it is unmounted before it has been initialised (the fraction the old family is visible before useEffect fetches new data)
      dispatch(resetStatus());
    }
  }, [dispatch, itemFamilyId]);

  useTitle(itemFamily?.draft?.name);

  return (
    <>
      <TopSection>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <BreadCrumb items={breadcrumbItems} />
          </Grid>
          <Grid item>
            {itemFamily.draft.code ? <PinButton itemCode={itemFamily.draft.code} itemName={itemFamily.draft.name} itemOwner={itemOwnerId} itemType="itemFamily" /> : null }
          </Grid>
        </Grid>
      </TopSection>

      <Box p={3.5}>
        <StatusTabs tabs={statusTabs} />
      </Box>

      <Box p={3.5} pb="70px">
        {fetchStatus === 'loading' ? <Loading /> : null}
        {fetchStatus === 'failed' ? <Alert severity="error">{t(`errors:${fetchError}`)}</Alert> : null}
        {fetchStatus === 'succeeded'
          ?
          <Route path={`${routeMatch.path}/:status?`} render={routeProps => {
            const status = routeProps.match.params.status || 'draft'; // default to draft (default is the one without url param)
            const data = itemFamily[status];
            const Component = ownerSpecificComponents[itemOwnerId];

            if(Component) {
              // Key forces it to re-mount when status changes
              return <Component key={status} status={status} itemFamily={data} canEdit={canEdit} canEditRoleBasedFields={canEditRoleBasedFields} />;
            } else {
              return <Alert severity="error">View not implemented for owner {itemOwnerId}</Alert>;
            }
          }}
          />
          :
          null
        }
      </Box>
    </>
  );
};

export default ItemFamily;
